import type { ExtensionInstallationPreview } from "@api-client";
import { Button } from "@sylviq/ui-react-web/components/ui/button";
import { Dialog } from "@sylviq/ui-react-web/components/ui/dialog";
import { useLayoutEffect, useRef, useState } from "react";
import { installBuildExtension, previewBuildInstallation, type DirectBuildInstallationResponse } from "@/lib/api";
import { directInstallationError, installationPreviewError } from "./installation-preview-error";

type DirectBuildInstallationProps = {
  organizationId: string;
  userId: string;
  packageId: string;
  publisher: string;
  packageKey: string;
  version: string;
  targetApps: string[] | "all";
  permissions?: unknown;
  onInstalled: (installation: DirectBuildInstallationResponse) => void;
};

type DependencyReview = {
  scopeKey: string;
  preview: ExtensionInstallationPreview;
};

function capabilityLabel(value: unknown): string | null {
  if (typeof value !== "object" || value === null || Array.isArray(value)) return null;
  const capability = value as Record<string, unknown>;
  if (
    typeof capability.resource !== "string" || !capability.resource.trim() ||
    typeof capability.action !== "string" || !capability.action.trim() ||
    !(capability.app === undefined || capability.app === null || typeof capability.app === "string") ||
    !(capability.egress_origin === undefined || capability.egress_origin === null || typeof capability.egress_origin === "string")
  ) return null;
  const app = typeof capability.app === "string" ? ` · ${capability.app}` : "";
  const origin = typeof capability.egress_origin === "string" ? ` · ${capability.egress_origin}` : "";
  return `${capability.resource} · ${capability.action}${app}${origin}`;
}

export function capabilitySummary(value: unknown): { valid: boolean; labels: string[] } {
  if (!Array.isArray(value)) return { valid: false, labels: [] };
  const labels = value.map(capabilityLabel);
  if (labels.some((label) => label === null)) return { valid: false, labels: [] };
  return { valid: true, labels: labels.filter((label): label is string => label !== null) };
}

export function installationTargetApps(manifest: unknown): string[] | "all" {
  if (typeof manifest !== "object" || manifest === null || Array.isArray(manifest)) return [];
  const record = manifest as Record<string, unknown>;
  const targetApps = record.target_apps ?? record.targetApps;
  if (targetApps === "all") return "all";
  if (!Array.isArray(targetApps)) return [];
  return [...new Set(targetApps.filter((app): app is string => typeof app === "string" && app.trim().length > 0))];
}

export function installationCapabilities(manifest: unknown): unknown {
  if (typeof manifest !== "object" || manifest === null || Array.isArray(manifest)) return undefined;
  const record = manifest as Record<string, unknown>;
  return record.requested_capabilities ?? record.capabilities;
}

function isValidInstallationReceipt(value: unknown): value is DirectBuildInstallationResponse {
  if (typeof value !== "object" || value === null || Array.isArray(value)) return false;
  const receipt = value as Record<string, unknown>;
  return receipt.status === "approved" &&
    typeof receipt.id === "string" &&
    /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(receipt.id);
}

export function DirectBuildInstallation({
  organizationId,
  userId,
  packageId,
  publisher,
  packageKey,
  version,
  targetApps,
  permissions,
  onInstalled,
}: DirectBuildInstallationProps) {
  const scopeKey = JSON.stringify([organizationId, organizationId, userId, packageId, version]);
  const scopeKeyRef = useRef(scopeKey);
  scopeKeyRef.current = scopeKey;
  const sequence = useRef(0);
  const requestController = useRef<AbortController | null>(null);
  const busy = useRef(false);
  const [busyState, setBusyState] = useState(false);
  const [review, setReview] = useState<DependencyReview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useLayoutEffect(() => {
    sequence.current += 1;
    requestController.current?.abort();
    requestController.current = null;
    busy.current = false;
    setBusyState(false);
    setReview(null);
    setError(null);
    setNotice(null);
    return () => {
      sequence.current += 1;
      requestController.current?.abort();
      requestController.current = null;
    };
  }, [scopeKey]);

  const selectedApps = targetApps === "all" ? [] : [...targetApps];
  const missingDependencies = review?.preview.dependencies.filter((item) => item.status === "missing") ?? [];
  const missingDependencyPermissions = missingDependencies.map((dependency) => ({
    dependency,
    permissions: capabilitySummary(dependency.requested_capabilities),
  }));
  const missingDependencyPermissionsVerified = missingDependencyPermissions.every(({ permissions }) => permissions.valid);
  const permissionsSummary = capabilitySummary(permissions);

  const installReviewedPlan = async (
    preview: ExtensionInstallationPreview,
    expectedScope: string,
    requestSequence: number,
  ) => {
    if (scopeKeyRef.current !== expectedScope || sequence.current !== requestSequence) return;
    busy.current = true;
    setBusyState(true);
    setError(null);
    setReview(null);
    const controller = new AbortController();
    requestController.current = controller;
    try {
      const result = await installBuildExtension(
        organizationId,
        {
          organization_id: organizationId,
          package_id: packageId,
          version,
          apps: preview.apps,
        },
        preview.plan_digest,
        controller.signal,
      );
      if (scopeKeyRef.current !== expectedScope || sequence.current !== requestSequence) return;
      if (!isValidInstallationReceipt(result)) {
        setError("The installation could not be confirmed (invalid_receipt). Check Installed extensions before trying again.");
        return;
      }
      setNotice("Extension installed.");
      onInstalled(result);
    } catch (cause) {
      if (scopeKeyRef.current !== expectedScope || sequence.current !== requestSequence) return;
      setError(directInstallationError(cause));
    } finally {
      if (sequence.current === requestSequence) {
        busy.current = false;
        setBusyState(false);
        if (requestController.current === controller) requestController.current = null;
      }
    }
  };

  const beginInstall = async () => {
    if (busy.current || targetApps === "all" || selectedApps.length === 0) return;
    busy.current = true;
    setBusyState(true);
    setError(null);
    setNotice(null);
    const expectedScope = scopeKey;
    const requestSequence = ++sequence.current;
    const controller = new AbortController();
    requestController.current = controller;
    try {
      const preview = await previewBuildInstallation(
        organizationId,
        {
          organization_id: organizationId,
          package_id: packageId,
          version,
          apps: selectedApps,
        },
        controller.signal,
      );
      if (scopeKeyRef.current !== expectedScope || sequence.current !== requestSequence) return;
      if (
        preview.package_id !== packageId ||
        preview.version !== version ||
        preview.apps.length !== selectedApps.length ||
        new Set(preview.apps).size !== preview.apps.length ||
        preview.apps.some((app) => !selectedApps.includes(app)) ||
        preview.dependencies.some((dependency) => dependency.apps.some((app) => !preview.apps.includes(app)))
      ) {
        throw new Error("Preview context mismatch");
      }
      const missing = preview.dependencies.some((dependency) => dependency.status === "missing");
      if (missing) {
        setReview({ scopeKey: expectedScope, preview });
        busy.current = false;
        setBusyState(false);
        if (requestController.current === controller) requestController.current = null;
        return;
      }
      await installReviewedPlan(preview, expectedScope, requestSequence);
    } catch (cause) {
      if (scopeKeyRef.current !== expectedScope || sequence.current !== requestSequence) return;
      setError(installationPreviewError(cause));
      busy.current = false;
      setBusyState(false);
      if (requestController.current === controller) requestController.current = null;
    }
  };

  const confirmInstall = () => {
    if (!review || review.scopeKey !== scopeKey || busy.current) return;
    const requestSequence = sequence.current;
    void installReviewedPlan(review.preview, review.scopeKey, requestSequence);
  };

  return (
    <div className="space-y-3">
      <section aria-label="Supported apps" className="space-y-1 text-sm">
        <h3 className="font-medium">Supported apps</h3>
        <p className="text-[var(--muted-foreground)]">
          {targetApps === "all" ? "All apps (organization context required)" : targetApps.join(", ") || "None declared"}
        </p>
      </section>
      <section aria-label="Declared permissions" className="space-y-1 text-sm">
        <h3 className="font-medium">Requested permissions</h3>
        {permissionsSummary.valid && permissionsSummary.labels.length > 0 ? (
          <ul className="list-inside list-disc text-[var(--muted-foreground)]">
            {permissionsSummary.labels.map((permission, index) => <li key={`${permission}-${index}`}>{permission}</li>)}
          </ul>
        ) : permissionsSummary.valid ? (
          <p className="text-[var(--muted-foreground)]">No organization capabilities requested.</p>
        ) : (
          <p className="text-[var(--danger)]" role="status">Permissions unavailable; this release cannot be installed until its declaration is verified.</p>
        )}
      </section>
      {permissionsSummary.valid ? (
        <p className="text-sm">Install grants the requested permissions for the supported apps.</p>
      ) : null}
      {targetApps === "all" ? (
        <p className="text-sm text-[var(--muted-foreground)]" role="status">
          Install is unavailable until this organization has a known app context for the wildcard target.
        </p>
      ) : (
        <Button
          aria-label={`Install ${publisher}/${packageKey} version ${version}`}
          disabled={busyState || selectedApps.length === 0 || !permissionsSummary.valid}
          onClick={() => void beginInstall()}
          size="sm"
          variant="outline"
        >
          {busyState ? "Installing..." : "Install"}
        </Button>
      )}
      {error ? <p className="text-sm text-[var(--danger)]" role="alert">{error}</p> : null}
      {notice ? <p className="text-sm" role="status">{notice}</p> : null}
      <Dialog
        description="Installing this extension also installs the missing required extensions and grants the permissions shown below for their supported apps."
        onOpenChange={(open) => {
          if (!open && !busy.current) setReview(null);
        }}
        open={Boolean(review && review.scopeKey === scopeKey)}
        title="Confirm required extensions"
      >
        {review?.scopeKey === scopeKey ? (
          <div className="space-y-4">
            <p className="text-sm">{publisher}/{packageKey} · version {version}</p>
            <section aria-label="Missing required extensions" className="space-y-2">
              <h3 className="font-medium">Missing required extensions</h3>
              <ul className="space-y-2 text-sm">
                {missingDependencyPermissions.map(({ dependency, permissions: dependencyPermissions }) => (
                  <li className="rounded-md border border-[var(--border)] p-3" key={dependency.package_id}>
                    <p>{dependency.publisher}/{dependency.key} · {dependency.version}</p>
                    <p className="text-[var(--muted-foreground)]">Apps: {dependency.apps.join(", ")}</p>
                    <p className="mt-2 font-medium">Requested permissions</p>
                    {dependencyPermissions.valid ? (
                      dependencyPermissions.labels.length > 0 ? (
                        <ul className="list-inside list-disc text-[var(--muted-foreground)]">
                          {dependencyPermissions.labels.map((permission, index) => (
                            <li key={`${permission}-${index}`}>{permission}</li>
                          ))}
                        </ul>
                      ) : <p className="text-[var(--muted-foreground)]">No organization capabilities requested.</p>
                    ) : (
                      <p className="text-[var(--danger)]">Permissions unavailable in the dependency preview.</p>
                    )}
                  </li>
                ))}
              </ul>
            </section>
            <div className="flex justify-end gap-2">
              <Button
                aria-label="Cancel dependency confirmation"
                onClick={() => setReview(null)}
                variant="outline"
              >
                Cancel
              </Button>
              <Button
                aria-label="Confirm install with dependencies"
                disabled={busyState || !missingDependencyPermissionsVerified}
                onClick={confirmInstall}
              >
                Install with dependencies
              </Button>
            </div>
          </div>
        ) : null}
      </Dialog>
    </div>
  );
}
